import { ConversionBlock } from '@/entities/conversion'
import {
  PaymentRequisiteToStatus,
  PaymentRequisitIcon,
} from '@/entities/paymentRequisit'
import { EnrichedRequisitesModel } from '@/features/requisites'
import EditIcon from '@/shared/assets/icons/edit.svg?react'
import { BaseButton, BaseCheckbox } from '@/shared/components'
import { BaseProgressBar } from '@/shared/components/BaseProgressBar/BaseProgressBar'
import { BankIcon } from '@/shared/components/icons/banks/BankIcon'
import { formatterRubWithCurrency } from '@/shared/utils'
import { ColumnDef } from '@tanstack/react-table'
import clsx from 'clsx'
import { formatRequisite } from '@/shared/lib'

interface RequisitColumnsProps {
  onEdit?: (id: string) => void
}

export const requisitColumns = ({
  onEdit,
}: RequisitColumnsProps): ColumnDef<EnrichedRequisitesModel>[] => [
  {
    id: 'select',
    accessorKey: 'select',
    header: ({ table }) => (
      <div
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        <BaseCheckbox
          checked={table.getIsAllPageRowsSelected()}
          onChange={(value) => table.toggleAllPageRowsSelected(value)}
        />
      </div>
    ),
    cell: ({ row }) => (
      <div
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
        }}
      >
        <BaseCheckbox
          checked={row.getIsSelected()}
          onChange={(value) => row.toggleSelected(value)}
        />
      </div>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'expand',
    accessorKey: 'expand',
    header: '',
    cell: () => null,
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'detail',
    header: 'Реквизит',
    cell: ({ row }) => {
      const requisit = row.original
      const profile = requisit?.paymentRequisitProfiles?.[0]

      const paymentRequisit = profile ? formatRequisite(profile.value ?? '') : '—'

      return (
        <div className="flex items-center gap-3">
          <BankIcon
            bankId={requisit.bankId}
            status={
              requisit.isBlocked
                ? 'stopped'
                : requisit.isDeleted
                  ? 'canceled'
                  : requisit.isDisabledByUser
                    ? 'stopped'
                    : 'active'
            }
            className="w-[40px] h-[40px]"
          />
          <div className="flex flex-col">
            <span className="font-medium text-base">
              {`${requisit.personSurname ?? ''} ${requisit.personName ?? ''} ${requisit.personMiddlename ?? ''}`}
            </span>
            <span className="text-text-secondary text-sm">{paymentRequisit}</span>
          </div>
        </div>
      )
    },
  },
  {
    id: 'active',
    accessorKey: 'active',
    header: 'Активные',
    cell: ({ row }) => {
      const activeAccounts = row.original.paymentRequisitProfiles
      return (
        <div className="flex items-center gap-2">
          {activeAccounts?.map((account) => (
            <div
              key={account.id}
              className={clsx(
                'flex items-center gap-1 px-2 border border-card-border dark:border-card-border-dark rounded-lg',
                {
                  'bg-[var(--status-green-fill)]': !account.isBlocked && !account.isDisabledByUser,
                  'bg-[var(--status-grey-fill)]': account.isBlocked || account.isDisabledByUser,
                }
              )}
            >
              <PaymentRequisitIcon
                className="w-4"
                accountType={account.requisitType}
                status={
                  account.isDisabledByUser || account.isBlocked
                    ? PaymentRequisiteToStatus.Disabled
                    : PaymentRequisiteToStatus.Active
                }
              />
              <span
                className={clsx('text-xs', {
                  'text-text-verificated': !account.isBlocked && !account.isDisabledByUser,
                  'text-text-secondary': account.isBlocked || account.isDisabledByUser,
                })}
              >{`... ${account.value.slice(-4)}`}</span>
            </div>
          ))}
        </div>
      )
    },
  },

  {
    id: 'maxDailyLimit',
    accessorKey: 'maxDailyLimit',
    header: 'Суточный лимит',
    cell: ({ row }) => {
      const { dailyStats, maxDailyLimit } = row.original
      const totalUsed = dailyStats?.reduce((sum, s) => sum + (s?.amountInRub ?? 0), 0) ?? 0

      return (
        <BaseProgressBar
          variant="table"
          currentBalance={totalUsed}
          totalBalance={maxDailyLimit ?? 0}
        />
      )
    },
  },
  {
    id: 'conversion',
    accessorKey: 'conversion',
    header: 'Конверсия',
    cell: ({ row }) => {
      const stats = row.original.totalStats ?? []
      const totalIn = stats.reduce((sum, s) => sum + (s?.counterIn ?? 0), 0)
      const totalSuccess = stats.reduce((sum, s) => sum + (s?.counterInSuccess ?? 0), 0)

      return <ConversionBlock current={totalSuccess} goal={totalIn} variant="table" />
    },
  },
  {
    id: 'amountInRub',
    accessorKey: 'amountInRub',
    header: 'Общий оборот',
    cell: ({ row }) => {
      const { totalStats } = row.original
      const amountInRub = totalStats?.reduce((sum, s) => sum + (s?.amountInRub ?? 0), 0) ?? 0
      return <span className="">{formatterRubWithCurrency.format(amountInRub)}</span>
    },
  },

  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      return (
        <div className="flex gap-3">
          <BaseButton
            variant="ghost"
            size="icon"
            onClick={(event) => {
              event.stopPropagation()
              event.preventDefault()
              onEdit?.(row.original.id)
            }}
          >
            <EditIcon className="text-text-secondary" />
          </BaseButton>
        </div>
      )
    },
  },
]
